import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

interface MetricSet {
  weekdays: number;
  weekends: number;
}

interface WeekdaysVsWeekendsAdherenceProps {
  calories?: MetricSet;
  protein?: MetricSet;
  carbs?: MetricSet;
  fat?: MetricSet;
}

const EMPTY: MetricSet = { weekdays: 0, weekends: 0 };

const COLORS = {
  calories: "#A78BFA",  // purple
  protein: "#60A5FA",   // blue
  carbs: "#FBBF24",     // yellow/orange
  fat: "#F472B6",       // pink
} as const;

type MacroKey = keyof typeof COLORS;

interface MacroCardProps {
  title: string;
  unit: string;
  color: string;
  data: MetricSet;
  diffUnit: string;
}

const formatNumber = (n: number) => n.toLocaleString();

const MacroCard: React.FC<MacroCardProps> = ({ title, unit, color, data, diffUnit }) => {
  const diff = data.weekends - data.weekdays;
  const isUp = diff >= 0;
  const chartData = [
    { day: "Weekdays", value: data.weekdays },
    { day: "Weekends", value: data.weekends },
  ];
  const maxValue = Math.max(data.weekdays, data.weekends);
  // Ensure a non-zero domain so the axis stays anchored when both values are 0.
  const yMax = maxValue > 0 ? Math.ceil(maxValue * 1.15) : 10;

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col">
      {/* Sub-title */}
      <div className="text-center mb-2">
        <span className="text-[11px] 3xl:text-[13px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          {title}
        </span>{" "}
        <span className="text-[10px] 3xl:text-[12px] text-[#8E8E93] font-inter normal-case tracking-normal">
          ({unit})
        </span>
      </div>

      {/* Chart */}
      <div className="flex-1">
        <ResponsiveContainer width="100%" height={140}>
          <BarChart
            data={chartData}
            barCategoryGap="8%"
            margin={{ top: 20, right: 8, left: 8, bottom: 0 }}
          >
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              dy={6}
            />
            <YAxis hide domain={[0, yMax]} />
            <Bar
              dataKey="value"
              fill={color}
              radius={[4, 4, 0, 0]}
              maxBarSize={100}
              minPointSize={2}
              isAnimationActive={true}
            >
              {chartData.map((_, i) => (
                <Cell key={`c-${i}`} fill={color} />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                content={(props: any) => {
                  const { x, y, width, value } = props;
                  if (value == null) return null;
                  return (
                    <text
                      x={x + width / 2}
                      y={y - 6}
                      textAnchor="middle"
                      fill="#1F1F1F"
                      fontSize={12}
                      fontFamily="Inter, sans-serif"
                      fontWeight={600}
                    >
                      {formatNumber(value)}
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Footer */}
      <div className="text-[12px] 3xl:text-[13px] text-[#8D8D8D] font-inter text-center mt-1">
        <span
          className="font-semibold"
          style={{ color: isUp ? "#20BCBB" : "#FF3B30" }}
        >
          {isUp ? "+" : ""}
          {diff} {diffUnit}
        </span>{" "}
        on weekends
      </div>
    </div>
  );
};

const WeekdaysVsWeekendsAdherence: React.FC<WeekdaysVsWeekendsAdherenceProps> = ({
  calories = EMPTY,
  protein = EMPTY,
  carbs = EMPTY,
  fat = EMPTY,
}) => {
  const legend: { label: string; color: string }[] = [
    { label: "Calories", color: COLORS.calories },
    { label: "Adj Protein", color: COLORS.protein },
    { label: "Carb", color: COLORS.carbs },
    { label: "Fat", color: COLORS.fat },
  ];

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Weekdays vs Weekends Adherence
        </span>
        <div className="flex items-center gap-4">
          {legend.map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-full inline-block"
                style={{ background: l.color }}
              />
              <span className="text-[11px] 3xl:text-[13px] text-[#1F1F1F] font-inter">
                {l.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-cards grid */}
      <div className="grid grid-cols-4 gap-4">
        <MacroCard
          title="Calories Intake"
          unit="avg/day"
          color={COLORS.calories}
          data={calories}
          diffUnit="kcal/day"
        />
        <MacroCard
          title="Adj Protein Intake"
          unit="avg/day"
          color={COLORS.protein}
          data={protein}
          diffUnit="g/day"
        />
        <MacroCard
          title="Carbs Intake"
          unit="avg/day"
          color={COLORS.carbs}
          data={carbs}
          diffUnit="g/day"
        />
        <MacroCard
          title="Fat Intake"
          unit="avg/day"
          color={COLORS.fat}
          data={fat}
          diffUnit="g/day"
        />
      </div>
    </div>
  );
};

export default WeekdaysVsWeekendsAdherence;
